import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  MapPin, Calendar, Users, ArrowRight, Search, Filter, 
  Briefcase, Home, DollarSign, Activity, Monitor, Heart, Shield, X, 
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotification } from '../context/NotificationContext';


const Marketplace = () => {
  // Initialize with empty arrays to prevent immediate crashes
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const scrollContainerRef = useRef(null);
  
  // PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);

  // Search Filters State
  const [filters, setFilters] = useState({
    location: '',
    urgency: '',
    dateStart: '',
    dateEnd: ''
  });

  // STATE FOR PERSISTENT SELECTION
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);

  const navigate = useNavigate();
  const { notify } = useNotification();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  // Inject Custom Font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // LOAD SELECTED LEADS FROM STORAGE ON MOUNT
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('my_selected_leads') || '[]');
    setSelectedLeadIds(stored);
  }, []);

  // Categories Configuration
  const categories = [
    { name: 'All', icon: <Filter size={16} /> },
    { name: 'Commercial Cleaning', id: 'commercial-cleaning', icon: <Briefcase size={16} /> },
    { name: 'Janitorial Services', id: 'janitorial', icon: <Shield size={16} /> },
    { name: 'Real Estate', id: 'real-estate', icon: <Home size={16} /> },
    { name: 'Financial Services', id: 'financial', icon: <DollarSign size={16} /> },
    { name: 'Healthcare', id: 'healthcare', icon: <Heart size={16} /> },
    { name: 'Technology', id: 'technology', icon: <Monitor size={16} /> },
    { name: 'MCA', id: 'mca', icon: <Activity size={16} /> },
  ];

  // Fetch leads whenever Page, Category, or Filters change
  useEffect(() => {
    fetchLeads();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, selectedCategory, filters.urgency]); 

  // Filter Logic (Client-side for Location/Date, Server-side for Category/Urgency)
  useEffect(() => {
    // FIX: Ensure result is an array even if leads is undefined
    let result = leads || [];

    // FILTER OUT ALREADY SELECTED LEADS
    if (selectedLeadIds.length > 0) {
      result = result.filter(l => !selectedLeadIds.includes(String(l.id)));
    }

    // Filter by Search Inputs (Client Side for now)
    if (filters.location) {
      const loc = filters.location.toLowerCase();
      // Ensure city/state exist before checking to avoid crashes
      result = result.filter(l => 
        (l.city && l.city.toLowerCase().includes(loc)) || 
        (l.state && l.state.toLowerCase().includes(loc))
      );
    }
    
    // Date Range Logic (Inclusive)
    if (filters.dateStart) {
      result = result.filter(l => l.appointment_date >= filters.dateStart);
    }
    if (filters.dateEnd) {
      result = result.filter(l => l.appointment_date <= filters.dateEnd);
    }

    setFilteredLeads(result);
  }, [leads, filters, selectedLeadIds]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      // Build Query String for Server-Side Pagination & Filtering
      const params = new URLSearchParams();
      params.append('page', currentPage);
      
      if (selectedCategory !== 'All') params.append('category', selectedCategory);
      if (filters.urgency) params.append('urgency', filters.urgency);
      
      const response = await api.get(`marketplace/?${params.toString()}`);
      
      // Handle Django Pagination Response
      // FIX: Use '|| []' to prevent setting leads to undefined if results key is missing
      setLeads(response.data.results || []);
      setNextPage(response.data.next);
      setPrevPage(response.data.previous);
      setTotalPages(Math.ceil((response.data.count || 0) / 15)); // Assuming page size is 15

    } catch (error) {
      console.error("Error fetching leads", error);
      setLeads([]); // FIX: Set empty array on error so app doesn't crash
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const clearFilters = () => {
    setFilters({ location: '', urgency: '', dateStart: '', dateEnd: '' });
    setSelectedCategory('All');
    setCurrentPage(1);
  };

  const handleViewMore = (leadId) => {
    if (!token) {
      notify('info', 'Please sign in to view full lead details.');
      navigate('/login');
    } else if (Number(role) === 2) {
      const newSelection = [...selectedLeadIds, String(leadId)];
      localStorage.setItem('my_selected_leads', JSON.stringify(newSelection));
      setSelectedLeadIds(newSelection);

      navigate(`/company/dashboard?selected=${leadId}`);
      notify('success', 'Lead selected! Check your dashboard.');
    } else {
      notify('error', 'Only Company accounts can purchase leads.');
    }
  };

  // Scroll Logic for Categories
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const { current } = scrollContainerRef;
      const scrollAmount = 300;
      if (direction === 'left') {
        current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-[Poppins]">
      
      {/* --- HERO SECTION --- */}
      <div className="relative bg-gradient-to-br from-[#2E86DE] to-[#0f2c4d] text-white overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full filter blur-3xl transform translate-x-1/3 -translate-y-1/3 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#F39C12] opacity-10 rounded-full filter blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 text-center">
          <motion.h1 
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-tight drop-shadow-lg"
          >
            Access <span className="text-[#F39C12]">Premium</span> Leads
          </motion.h1>
          <motion.p 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-lg md:text-2xl text-blue-100 max-w-3xl mx-auto font-light leading-relaxed"
          >
            Stop chasing cold prospects. Unlock verified, high-intent appointments ready to close.
          </motion.p>
        </div>
      </div>

      {/* --- CATEGORIES & FILTERS --- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
        
        {/* Categories Scroller */}
        <div className="relative group">
          <button 
            onClick={() => scroll('left')} 
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg text-gray-600 hover:text-primary hover:scale-110 transition opacity-0 group-hover:opacity-100 -ml-4"
          >
            <ChevronLeft size={24} />
          </button>
          
          <motion.div 
            ref={scrollContainerRef}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex overflow-x-auto pb-4 gap-4 scrollbar-hide scroll-smooth px-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => { setSelectedCategory(cat.id || 'All'); setCurrentPage(1); }}
                className={`flex-shrink-0 flex items-center px-6 py-3 rounded-2xl text-sm font-bold whitespace-nowrap transition-all duration-300 border ${
                  selectedCategory === (cat.id || 'All')
                    ? 'bg-[#F39C12] text-white border-[#F39C12] shadow-xl transform -translate-y-1'
                    : 'bg-white text-gray-600 border-gray-100 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
                }`}
              >
                <span className="mr-2">{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </motion.div>

          <button 
            onClick={() => scroll('right')} 
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg text-gray-600 hover:text-primary hover:scale-110 transition opacity-0 group-hover:opacity-100 -mr-4"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Horizontal Filter Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/90 backdrop-blur-md p-2 rounded-2xl shadow-xl border border-gray-100 mt-6 flex flex-col md:flex-row gap-3 items-center"
        >
          <div className="relative flex-1 w-full">
            <MapPin size={18} className="absolute left-4 top-3.5 text-primary" />
            <input 
              type="text" 
              name="location" 
              placeholder="Search City or State..." 
              className="w-full pl-11 pr-4 py-3 border-none rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary transition-all font-medium text-gray-700"
              value={filters.location}
              onChange={handleFilterChange}
            />
          </div>

          <div className="w-full md:w-48 bg-gray-50 rounded-xl px-2">
            <select 
              name="urgency" 
              className="w-full p-3 border-none bg-transparent focus:ring-0 text-sm font-semibold text-gray-700 cursor-pointer"
              value={filters.urgency}
              onChange={handleFilterChange}
            >
              <option value="">Urgency: Any</option>
              <option value="high">🔥 High</option>
              <option value="medium">⚡ Medium</option>
              <option value="low">🕒 Low</option>
            </select>
          </div>

          <div className="flex gap-2 w-full md:w-auto bg-gray-50 p-1.5 rounded-xl items-center">
            <div className="px-2 text-xs font-bold text-gray-400">FROM</div>
            <input type="date" name="dateStart" className="w-full bg-transparent border-none text-xs text-gray-600 focus:ring-0 font-medium" onChange={handleFilterChange} value={filters.dateStart} />
            <div className="px-2 text-xs font-bold text-gray-400">TO</div>
            <input type="date" name="dateEnd" className="w-full bg-transparent border-none text-xs text-gray-600 focus:ring-0 font-medium" onChange={handleFilterChange} value={filters.dateEnd} />
          </div>

          <button 
            onClick={clearFilters}
            className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
            title="Reset"
          >
            <X size={20} />
          </button>
        </motion.div>
      </div>

      {/* --- LEADS GRID --- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="flex justify-between items-end mb-8 border-b border-gray-200 pb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Available Opportunities</h2>
            {/* FIX: Add '?.' to safely read length */}
            <p className="text-sm text-gray-500 mt-1">Showing {filteredLeads?.length || 0} results</p>
          </div>
          {loading && <span className="text-primary animate-bounce text-sm font-bold">Fetching live data...</span>}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => <div key={i} className="h-72 bg-gray-200 rounded-3xl animate-pulse"></div>)}
          </div>
        ) : filteredLeads?.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-inner">
            <Search size={64} className="mx-auto text-gray-200 mb-6" />
            <h3 className="text-xl font-bold text-gray-900">No leads found</h3>
            <p className="text-gray-500">Try adjusting your filters to find more results.</p>
            <button onClick={clearFilters} className="mt-6 text-primary font-extrabold hover:underline">Clear all filters</button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <AnimatePresence>
                {/* FIX: Add '?.' to safely map over items */}
                {filteredLeads?.map((lead, index) => (
                  <motion.div 
                    key={lead.id} 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    exit={{ opacity: 0 }}
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    className="bg-white rounded-3xl shadow-lg border border-gray-100 flex flex-col overflow-hidden group relative hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="p-7 flex-1">
                      {/* Header: Category & Urgency */}
                      <div className="flex justify-between items-start mb-6">
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-extrabold bg-blue-50 text-blue-600 uppercase tracking-wider">
                          {lead.category?.replace('-', ' ') || 'General'}
                        </span>
                        {lead.urgency === 'high' && (
                          <span className="flex items-center text-red-500 text-[10px] font-black bg-red-50 px-2 py-1 rounded-lg">
                            <Activity size={12} className="mr-1" /> URGENT
                          </span>
                        )}
                      </div>
                      
                      {/* BLURRED REVEAL INTERFACE */}
                      <div className="relative mb-6">
                        <h3 className="text-2xl font-black text-gray-800 leading-tight filter blur-[6px] opacity-40 transition-all duration-300 group-hover:opacity-100 group-hover:blur-sm">
                          Confidential Client
                        </h3>
                        <div className="absolute inset-0 flex items-center justify-start opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                          <span className="bg-gray-900/90 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-2xl backdrop-blur-md transform translate-y-2 group-hover:translate-y-0 transition-transform">
                            Sign In to Reveal
                          </span>
                        </div>
                      </div>
                      
                      {/* Details */}
                      <div className="space-y-4">
                        {/* --- FIXED LOCATION SECTION: Use lead here inside the map --- */}
                        <div className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors">
                          <div className="p-2.5 bg-gray-50 rounded-xl mr-3 group-hover:bg-[#2E86DE] group-hover:text-white transition-colors duration-300">
                            <MapPin size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-bold uppercase">Location</p>
                            <span className="font-semibold text-sm">{lead.city || 'N/A'}, {lead.state || 'N/A'}, {lead.country || 'N/A'}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors">
                          <div className="p-2.5 bg-gray-50 rounded-xl mr-3 group-hover:bg-[#2E86DE] group-hover:text-white transition-colors duration-300">
                            <Calendar size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-bold uppercase">Appointment</p>
                            <span className="font-semibold text-sm">
                              {lead.appointment_date 
                                ? new Date(lead.appointment_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'Flexible'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors">
                          <div className="p-2.5 bg-gray-50 rounded-xl mr-3 group-hover:bg-[#2E86DE] group-hover:text-white transition-colors duration-300">
                            <Users size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-bold uppercase">Size</p>
                            <span className="font-semibold text-sm">{lead.facility_size || "Standard"}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer Price Action */}
                    <div className="bg-gray-50 px-7 py-5 border-t border-gray-100 flex items-center justify-between group-hover:bg-[#2E86DE]/5 transition-colors duration-500">
                      <div>
                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Price</p>
                        <p className="text-3xl font-black text-[#2E86DE]">${lead.price_company}</p>
                      </div>
                      
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleViewMore(lead.id)}
                        className="flex items-center bg-gradient-to-r from-[#2E86DE] to-blue-700 text-white px-6 py-3 rounded-xl text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        View More <ArrowRight size={16} className="ml-2" />
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* PAGINATION CONTROLS */}
            <div className="mt-12 flex justify-center items-center space-x-2">
              <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={!prevPage}
                className={`p-3 rounded-full ${!prevPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200 shadow-sm bg-white'}`}
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="flex items-center space-x-1">
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePageChange(idx + 1)}
                    className={`w-10 h-10 rounded-full font-bold text-sm transition-all ${
                      currentPage === idx + 1 
                        ? 'bg-primary text-white shadow-lg scale-110' 
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={!nextPage}
                className={`p-3 rounded-full ${!nextPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200 shadow-sm bg-white'}`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Marketplace;